import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import Footer from "../src/modules/core/Footer";
import Login from "../src/modules/core/Login";
import "./App.css";
import PrivateRoute from "./modules/core/PrivateRoute";
import ErrorBoundary from "./modules/error-boundaries/ErrorBoundary";
import { Box, CssBaseline } from "@mui/material";
import TokenRefresher from "./modules/core/TokenRefresher";
import Summary from "./modules/fiscalUpgrade/Accounts/Summary";
import { UserProvider } from "./modules/contexapi/UserContext";
import QBOSetup from "./modules/fiscalUpgrade/Qbo/QBOSetup";

import environment from "./environments/environment";
const langMap: Record<string, string> = {
  English: 'en',
  Spanish: 'es',
  en: 'en',
  es: 'es'
};
//User Management
const UserListing = lazy(() => import("./modules/fiscalUpgrade/UserManagment/UserListing"));
const UserRole = lazy(() => import("./modules/fiscalUpgrade/UserManagment/UserRole"));
const NewUser = lazy(() => import("./modules/fiscalUpgrade/UserManagment/NewUser"));
const UserReviewAndSubmit = lazy(() => import("./modules/fiscalUpgrade/UserManagment/UserReviewAndSubmit"));
const UserParticipantList = lazy(() => import("./modules/fiscalUpgrade/UserManagment/UserParticipantList"));
const EnterFundDetails = lazy(() => import("./modules/fiscalUpgrade/Funds/NewFundsCreation/EnterFundDetails"));
const FundAllocation = lazy(() => import("./modules/fiscalUpgrade/Funds/NewFundsCreation/FundAllocation"));
const ReviewAndSubmitFund = lazy(() => import("./modules/fiscalUpgrade/Funds/NewFundsCreation/ReviewAndSubmitFund"));
const FundTransferDetails = lazy(() => import("./modules/fiscalUpgrade/Funds/TranferFunds/FundTransferDetails"));
const ReviewAndSubmitTransferFund = lazy(() => import("./modules/fiscalUpgrade/Funds/TranferFunds/ReviewAndSubmitTransferFund"));
const WelcomePage = lazy(() => import("./modules/core/WelcomePage"));
const ParticipantsSummaryDetails = lazy(() => import("./modules/fiscalUpgrade/Participants/ParticipantsSummaryDetails"));
const EnterParticipantDetails = lazy(() => import("./modules/fiscalUpgrade/Participants/NewParticipantCreation/EnterParticipantDetails"));
const AddressDetails = lazy(() => import("./modules/fiscalUpgrade/Participants/NewParticipantCreation/AddressDetails"));
const ReviewAndSubmit = lazy(() => import("./modules/fiscalUpgrade/Participants/NewParticipantCreation/ReviewAndSubmit"));
const ParticipantIdDetails = lazy(() => import("./modules/fiscalUpgrade/Participants/NewParticipantCreation/ParticipantIdDetails"));
const FundSummary = lazy(() => import("./modules/fiscalUpgrade/Funds/FundDetailPage/FundSummary"));
const SpecialPayment = lazy(() => import("./modules/fiscalUpgrade/Special Payments/SpecialPayment"));
const SpecialPaymentReviewAndSubmit = lazy(() => import("./modules/fiscalUpgrade/Special Payments/SpecialPaymentReviewAndSubmit"));

import { CustomizeHeader } from "./modules/core/CustomizeHeader/CustomizeHeader";
import MiniDrawer from "./modules/core/CustomizeHeader/MiniDrawer";
import { TopMenuItems } from "./modules/core/CustomizeHeader/TopMenuItems";
import BudgetListing from "./modules/fiscalUpgrade/Financials/BudgetListing";
import ActualsListing from "./modules/fiscalUpgrade/Financials/ActualsListing";
import ContactInfo from "./modules/core/Contact/Contact";
import SummaryDetails from "./modules/fiscalUpgrade/Accounts/SummaryDetails";
const EnterLoanDetails = lazy(() => import("./modules/fiscalUpgrade/Loans/LoansSummary/NewLoanCreation/EnterLoanDetails"));
const ChooseLoanDetails = lazy(() => import("./modules/fiscalUpgrade/Loans/LoansSummary/NewLoanCreation/ChooseLoanDetails"));
const NewLoanReview = lazy(() => import("./modules/fiscalUpgrade/Loans/LoansSummary/NewLoanCreation/NewLoanReview"));
const LoanSummary = lazy(() => import("./modules/fiscalUpgrade/Loans/LoansSummary/LoanListing/LoanSummary"));
const MakePayments = lazy(() => import("./modules/fiscalUpgrade/Loans/LoansSummary/Make Payments/MakePayments"));
const LoanDetails = lazy(() => import("./modules/fiscalUpgrade/Loans/LoansSummary/LoanDetails"));
const MakePaymentsReview = lazy(() => import("./modules/fiscalUpgrade/Loans/LoansSummary/Make Payments/MakePaymentsReview"));
import { AppThemeProvider } from "./shared/Theme/AppContext";
import ProfileSettings from "./modules/core/ProfileSettings/ProfileSettings";
import ActualsDetails from "./modules/fiscalUpgrade/Financials/ActualsDetails";
import ActualsImport from "./modules/fiscalUpgrade/Financials/ActualsImport";
import ActualsReportView from "./modules/fiscalUpgrade/Financials/ActualsReportView";
import BudgetDetails from "./modules/fiscalUpgrade/Financials/BudgetDetails";
import BudgetImport from "./modules/fiscalUpgrade/Financials/BudgetImport";
import BudgetReportView from "./modules/fiscalUpgrade/Financials/BudgetReportView";
import NewBudget from "./modules/fiscalUpgrade/Financials/NewBudget";
import MonthlyListing from "./modules/fiscalUpgrade/MonthlyInterestCalculationHistory/MonthlyListing";
import MonthlyCalculations from "./modules/fiscalUpgrade/MonthlyInterestCalculationHistory/monthlyCalculations";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./store/store";
import AutoDebitSummaryListing from "./modules/fiscalUpgrade/Loans/AutoDebits/AutoDebitSummary";
import AutoDebitLoanDetails from "./modules/fiscalUpgrade/Loans/AutoDebits/LoanDetails";
import AutoDebitReviewAndSubmit from "./modules/fiscalUpgrade/Loans/AutoDebits/ReviewandSubmit";
import AutoDebitScheduleRule from "./modules/fiscalUpgrade/Loans/AutoDebits/AutoDebitScheduleRule";
import BudgetAccountDetails from "./modules/fiscalUpgrade/Financials/BudgetAccountDetails";
import RcboSummaryListing from "./modules/fiscalUpgrade/Invoices/RCBOInvoices/InvoiceSummaryListing";
import InvoiceDetails from "./modules/fiscalUpgrade/Invoices/RCBOInvoices/InvoiceDetails";
import MakePayment from "./modules/fiscalUpgrade/Invoices/RCBOInvoices/MakePayment";
import PaymentReviewAndSubmit from "./modules/fiscalUpgrade/Invoices/RCBOInvoices/PaymentSubmit";
import CreditLineListingSummary from "./modules/fiscalUpgrade/Loans/CreditLineSummary/CreditLineListing";
import BudgetImported from "./modules/fiscalUpgrade/Financials/BudgetImported";
import NewInvoice from "./modules/fiscalUpgrade/Invoices/ProjectInvoices/NewInvoice";
import CreditLineSubmit from "./modules/fiscalUpgrade/Loans/CreditLineSummary/CreditPaymentSubmit";
import CreditDetails from "./modules/fiscalUpgrade/Loans/CreditLineSummary/CreditDetails";
import NewCreditLine from "./modules/fiscalUpgrade/Loans/CreditLineSummary/NewCreditLine";
import CreditMakePayment from "./modules/fiscalUpgrade/Loans/CreditLineSummary/CreditMakePayment";
import NewDetailsSubmit from "./modules/fiscalUpgrade/Loans/CreditLineSummary/DetailsSubmit";
import Spinner from "./modules/core/Spinner";
import ActualsImported from "./modules/fiscalUpgrade/Financials/ActualsImported";
import AlertBox from "./modules/core/AlertBox";
import SessionTimeoutHandler from "./modules/core/SessionTimeoutHandler";
import InterceptorInitializer from "./modules/fiscalUpgrade/services/InterceptorInitializer";
import { getCurrentHolidayBackground } from "./modules/fiscalUpgrade/Utils/getCurrentHolidayBackground";
import ManualEntries from "./modules/fiscalUpgrade/ManualEntries/manualEntries";
import ScrollToTop from "./Scroll";
import { fetchThemeDetailsByClientId } from "./store/features/themeSlice";
import i18next from "i18next";
import BlackBaudLog from "./modules/fiscalUpgrade/SettingScreens/BlackBaudLog";
import BlackBaudTransactionLog from "./modules/fiscalUpgrade/SettingScreens/BlackBaudTransactionLog/BlackBaudTransactionLog";
import BlackBaudDetailsPage from "./modules/fiscalUpgrade/SettingScreens/BlackBaudTransactionLog/BlackBaudDetailsPage";
import BlackBaudConfiguration from "./modules/fiscalUpgrade/SettingScreens/BlackBaudTransactionLog/BlackBaudConfiguration";
import InvoicesPayments from "./modules/fiscalUpgrade/ManualEntries/invoicesPayments";
import InvoicesPaymentsTransactions from "./modules/fiscalUpgrade/ManualEntries/invoicesPaymentsTransactions";
import CreditLinePayments from "./modules/fiscalUpgrade/ManualEntries/creditLinePayments";  
import MonthlyReport from "./modules/PopUp/FiscalReports/MonthlyReport";
import FundsListing from "./modules/fiscalUpgrade/Funds/FundsListing";
const MoreInfo = lazy(() => import("./modules/fiscalUpgrade/Special Funds/MoreInfo"));

const ParticipantIntrestsSummary = lazy(() => import("./modules/fiscalUpgrade/ParticipantInterest/ParticipantInterestsSummary"));
const ParticipantDetails = lazy(() => import("./modules/fiscalUpgrade/ParticipantInterest/ParticipantDetails"));
const NewParticipantDetails = lazy(() => import("./modules/fiscalUpgrade/ParticipantInterest/NewParticipantDetails"));
const InvoiceReviewandSubmit = lazy(() => import("./modules/fiscalUpgrade/Invoices/ProjectInvoices/InvoiceReviewandSubmit"));
const SelectApprovers = lazy(() => import("./modules/fiscalUpgrade/Invoices/ProjectInvoices/SelectApprovers"));
const GlAdjustmentsListing = lazy(() => import("./modules/fiscalUpgrade/GLAdjustments/GlAdjustmentsListing"));
const EnterGLAdjustmentDetails = lazy(() => import("./modules/fiscalUpgrade/GLAdjustments/EnterGLAdjustmentDetails"));
const GLAdjustmentDetails = lazy(() => import("./modules/fiscalUpgrade/GLAdjustments/GLAdjustmentDetails"));
const SelectDLAccounts = lazy(() => import("./modules/fiscalUpgrade/GLAdjustments/SelectDLAccounts"));
const GLReviewSubmit = lazy(() => import("./modules/fiscalUpgrade/GLAdjustments/GLReviewSubmit"));
const GLAdminApproval = lazy(() => import("./modules/fiscalUpgrade/GLAdjustments/GLAdminApproval"));

const EnterInvoiceDetails = lazy(() => import("./modules/fiscalUpgrade/Invoices/ProjectInvoices/EnterInvoiceDetails"));
const RCBOSummary = lazy(() => import("./modules/fiscalUpgrade/RCBOAccountSummary/RCBOSummary"));
const SettingSummaryScreens = lazy(() => import('./modules/fiscalUpgrade/SettingScreens/SettingSummaryScreens'));
const AuditLog = lazy(() => import('./modules/fiscalUpgrade/SettingScreens/AuditLog/AuditLog'));
const NetSuiteLog = lazy(() => import('./modules/fiscalUpgrade/SettingScreens/NetSuite/NetSuiteLog'));
const QuickBooksLog = lazy(() => import('./modules/fiscalUpgrade/Qbo/QuickBooksLog'));
const SpecialFundsListing = lazy(() => import("./modules/fiscalUpgrade/Special Funds/SpecialFundsListing"));
const NewSpecialFunds = lazy(() => import("./modules/fiscalUpgrade/Special Funds/NewSpecialFunds"));
const SpecialFundDetailsPage = lazy(() => import("./modules/fiscalUpgrade/Special Funds/SpecialFundDetailsPage"));
const SpecialFundReviewAndSubmit = lazy(() => import("./modules/fiscalUpgrade/Special Funds/SpecialFundReviewAndSubmit"));
const SpecialFundHistory = lazy(() => import("./modules/fiscalUpgrade/Special Funds/SpecialFundHistory"));
const EditSpecialFundDetails = lazy(() => import("./modules/fiscalUpgrade/Special Funds/EditSpecialFundDetails"));


const ClientstatementReport = lazy(() => import('./modules/PopUp/FiscalReports/ClientstatementReport'));
const LoanStatementReport = lazy(() => import('./modules/PopUp/FiscalReports/LoanStatementReport'));
const ParticipantSummaryReport = lazy(() => import('./modules/PopUp/FiscalReports/ParticipantSummaryReport'));
const VendorInvoiceSummary = lazy(
  () => import("./modules/fiscalUpgrade/Invoices/Project/VendorInvoiceSummary")
);

const VendorInvoiceDetails = lazy(
  () => import("./modules/fiscalUpgrade/Invoices/Project/VendorInvoiceDetails")
);


const InvoiceTasksSummary = lazy(
  () => import("./modules/fiscalUpgrade/Invoices/Tasks/InvoiceTasksSummary")
);

const InvoiceTasksDetails = lazy(
  () => import("./modules/fiscalUpgrade/Invoices/Tasks/InvoiceTaksDetails")
);



function App() {
  const excludePaths = ["/", "/fiscal/", "/fiscal"];
  const [view, setView] = useState("");
  const [open, setOpen] = useState(true);
  const userDetails = useSelector((state: RootState) => state.auth);
  const profileDetails = useSelector((state: RootState) => state.profileSettings);
  const [showFooter, setShowFooter] = useState(false);
  const holiday = getCurrentHolidayBackground();
  //const resolvedBackgroundImage = backgroundImageMap[holiday?.backgroundGif || ''];
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  //const [resolvedBackgroundImage, setResolvedBackgroundImage] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const resolvedBackgroundImage = useSelector((state: any) => state.theme.resolvedBackgroundImage);
  const refreshFlag = useSelector((state: RootState) => state.theme.refreshFlag);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const checkContentSize = () => {
      const { scrollHeight, clientHeight } = el;
      if (scrollHeight <= clientHeight) {
        setShowFooter(true);
      } else {
        setShowFooter(false);
      }
    };
    checkContentSize();
    const observer = new MutationObserver(() => {
      checkContentSize();
    });
    observer.observe(el, { childList: true, subtree: true });
    window.addEventListener("resize", checkContentSize);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkContentSize);
    };
  }, []);

  useEffect(() => {
    i18next.changeLanguage(langMap[userDetails.language] || 'en');
    const handleBeforeUnload = (event: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const currentPath = window.location.pathname;
    if (!excludePaths.includes(currentPath)) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    setView(profileDetails.value)
  }, [profileDetails])

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (el) {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 1;
      setShowFooter(isBottom);
    }
  };

  useEffect(() => {
    if (userDetails && userDetails?.clientId && environment.generalSettings) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
      const dd = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${yyyy}-${mm}-${dd}`;
      dispatch(fetchThemeDetailsByClientId({ tDate: formattedDate, clientId: userDetails.clientId }));
    }
  }, [dispatch, refreshFlag]);

  return (
    <div className="App">
      <AppThemeProvider>
        <CssBaseline />
        <UserProvider>
          <Router basename={environment.ApplicationBaseName}>
            <ScrollToTop scrollRef={scrollContainerRef} />
            <InterceptorInitializer />
            <Suspense fallback={<Spinner />}>
              {userDetails.isLogin && <CustomizeHeader view={view} />}
              {view === "Top" ? (
                <TopMenuItems />
              ) : view === "Left" ? (
                <MiniDrawer open={open} setOpen={setOpen} />
              ) : null}

              {/* <MiniDrawer /> */}
              <Box
                sx={{
                  ...(view === "Left" && {
                    ml: { sm: open ? 30 : 9 }, // Adjust content margin when MiniDrawer is collapsed
                    transition: "margin 0.3s",
                  }),
                }}
              >
                <Box sx={{ mt: view === "Left" ? 9 : 0 }}>
                  <ContactInfo view={view} />
                </Box>
                {/* Content Container (Scrolls only inside Routes) */}
                <Box ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className="routes-container"
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    height: '100vh',
                    paddingLeft: view === "Left" ? 3 : '6.5%',
                    paddingRight: view === "Left" ? 4 : '6.5%',
                    backgroundImage: resolvedBackgroundImage && environment.generalSettings ? `url(${resolvedBackgroundImage})` : 'none',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}>
                  <Box sx={{ mb: 1 }}>
                    <AlertBox />
                  </Box>
                  {userDetails.isLogin && <SessionTimeoutHandler />}
                  <Box className="content-container" sx={{ paddingLeft: 2 }}></Box>
                  <ErrorBoundary>
                    <Box

                    >
                      <Routes>
                        <Route path="/" element={<Login setView={setView} />} />
                        <Route path="/welcomePage" element={<PrivateRoute element={WelcomePage} />} />
                        <Route path="/summary" element={<PrivateRoute element={Summary} />} />
                        <Route path="/summaryDetails" element={<PrivateRoute element={SummaryDetails} />} />
                        <Route path="/userListing" element={<PrivateRoute element={UserListing} />} />
                        <Route path="*" element={<Navigate to="/" />} />
                        <Route path="/enterFundDetails" element={<PrivateRoute element={EnterFundDetails} />} />
                        <Route path="/fundAllocation" element={<PrivateRoute element={FundAllocation} />} />
                        <Route path="/newFundCreation" element={<PrivateRoute element={ReviewAndSubmitFund} />} />
                        <Route path="/fundTransferDetails" element={<PrivateRoute element={FundTransferDetails} />} />
                        <Route path="/fundTransfer" element={<PrivateRoute element={ReviewAndSubmitTransferFund} />} />
                        <Route path="/participantsSummaryDetails" element={<PrivateRoute element={ParticipantsSummaryDetails} />} />
                        <Route path="/enterParticipantDetails" element={<PrivateRoute element={EnterParticipantDetails} />} />
                        <Route path="/addressDetails" element={<PrivateRoute element={AddressDetails} />} />
                        <Route path="/reviewAndSubmit" element={<PrivateRoute element={ReviewAndSubmit} />} />
                        <Route path="/userRoles" element={<PrivateRoute element={UserRole} />} />
                        <Route path="/ParticipantIdDetails" element={<PrivateRoute element={ParticipantIdDetails} />} />
                        <Route path="/fundSummary" element={<PrivateRoute element={FundSummary} />} />
                        <Route path="/specialPayment" element={<PrivateRoute element={SpecialPayment} />} />
                        <Route path="/specialPaymentReviewAndSubmit" element={<PrivateRoute element={SpecialPaymentReviewAndSubmit} />} />
                        <Route path="/enterLoanDetails" element={<PrivateRoute element={EnterLoanDetails} />} />
                        <Route path="/chooseLoanDetails" element={<PrivateRoute element={ChooseLoanDetails} />} />
                        <Route path="/newLoanReviewAndSubmit" element={<PrivateRoute element={NewLoanReview} />} />
                        <Route path="/loanSummary" element={<PrivateRoute element={LoanSummary} />} />
                        <Route path="/makePayments" element={<PrivateRoute element={MakePayments} />} />
                        <Route path="/loanDetails" element={<PrivateRoute element={LoanDetails} />} />
                        <Route path="/makePaymentReviewAndSubmit" element={<PrivateRoute element={MakePaymentsReview} />} />
                        <Route path="/userParticipant" element={<PrivateRoute element={UserParticipantList} />} />
                        <Route path="/userReviewAndSubmit" element={<PrivateRoute element={UserReviewAndSubmit} />} />
                        <Route path="/newUser" element={<PrivateRoute element={NewUser} />} />
                        <Route path="/budgetListing" element={<PrivateRoute element={BudgetListing} />} />
                        <Route path="/actualsListing" element={<PrivateRoute element={ActualsListing} />} />
                        <Route path="/budgetAccountDetails" element={<PrivateRoute element={BudgetAccountDetails} />} />
                        <Route path="/newBudget" element={<PrivateRoute element={NewBudget} />} />
                        <Route path="/actualsDetails" element={<PrivateRoute element={ActualsDetails} />} />
                        <Route path="/budgetDetails" element={<PrivateRoute element={BudgetDetails} />} />
                        <Route path="/actualsImport" element={<PrivateRoute element={ActualsImport} />} />
                        <Route path="/budgetImport" element={<PrivateRoute element={BudgetImport} />} />
                        <Route path="/budgetImported" element={<PrivateRoute element={BudgetImported} />} />
                        <Route path="/actualsImported" element={<PrivateRoute element={ActualsImported} />} />
                        <Route path="/actualsReportView" element={<PrivateRoute element={ActualsReportView} />} />
                        <Route path="/budgetReportView" element={<PrivateRoute element={BudgetReportView} />} />
                        <Route path="/manualEntries" element={<PrivateRoute element={ManualEntries} />} />
                        <Route path="/specialPayment" element={<PrivateRoute element={SpecialPayment} />} />
                        <Route path="/specialPaymentReviewAndSubmit" element={<PrivateRoute element={SpecialPaymentReviewAndSubmit} />} />
                        <Route path="/profileSettings" element={<PrivateRoute element={ProfileSettings} view={view} setView={setView} />} />
                        <Route path="/settingSummaryScreens" element={<PrivateRoute element={SettingSummaryScreens} />} />
                        <Route path="/auditLog" element={<PrivateRoute element={AuditLog} />} />
                        <Route path="/netSuiteLog" element={<PrivateRoute element={NetSuiteLog} />} />
                        <Route path="/quickBooksLog" element={<PrivateRoute element={QuickBooksLog} />} />
                        <Route path="/blackBaudLog" element={<PrivateRoute element={BlackBaudLog} />} />
                        <Route path="/rcboSummary" element={<PrivateRoute element={RCBOSummary} />} />
                        <Route path="/monthlyListing" element={<PrivateRoute element={MonthlyListing} />} />
                        <Route path="/monthlyCalculations" element={<PrivateRoute element={MonthlyCalculations} />} />
                        <Route path="/autoDebitListing" element={<PrivateRoute element={AutoDebitSummaryListing} />} />
                        <Route path="/autoDebitLoanDetails" element={<PrivateRoute element={AutoDebitLoanDetails} />} />
                        <Route path="/autoDebitReviewAndSubmit" element={<PrivateRoute element={AutoDebitReviewAndSubmit} />} />
                        <Route path="/autoDebitScheduleRule" element={<PrivateRoute element={AutoDebitScheduleRule} />} />
                        <Route path="/clientstatement" element={<PrivateRoute element={ClientstatementReport} />} />
                        <Route path="/loanstatement" element={<PrivateRoute element={LoanStatementReport} />} />
                        <Route path="/participantSummary" element={<PrivateRoute element={ParticipantSummaryReport} />} />
                        <Route path="/monthlyStatement" element={<PrivateRoute element={MonthlyReport} />} />
                        <Route path="/rcboInvoiceSummaryListing" element={<PrivateRoute element={RcboSummaryListing} />} />
                        <Route path="/newInvoice" element={<PrivateRoute element={NewInvoice} />} />
                        <Route path="/rcboInvoiceDetails" element={<PrivateRoute element={InvoiceDetails} />} />
                        <Route path="/rcboInvoicePayment" element={<PrivateRoute element={MakePayment} />} />
                        <Route path="/rcboInvoiceSubmit" element={<PrivateRoute element={PaymentReviewAndSubmit} />} />
                        <Route path="/creditLineSummaryListing" element={<PrivateRoute element={CreditLineListingSummary} />} />
                        <Route path="/creditLineMakePayment" element={<PrivateRoute element={CreditMakePayment} />} />
                        <Route path="/creditLinePaymentSubmit" element={<PrivateRoute element={CreditLineSubmit} />} />
                        <Route path="/creditLineDetails" element={<PrivateRoute element={CreditDetails} />} />
                        <Route path="/newCreditLine" element={<PrivateRoute element={NewCreditLine} />} />
                        <Route path="/creditLineReviewAndSubmit" element={<PrivateRoute element={NewDetailsSubmit} />} />
                        <Route path="/specialFundsListing" element={<PrivateRoute element={SpecialFundsListing} />} />
                        <Route path="/newSpecialFunds" element={<PrivateRoute element={NewSpecialFunds} />} />
                        <Route path="/specialFundDetailsPage" element={<PrivateRoute element={SpecialFundDetailsPage} />} />
                        <Route path="/specialFundReviewAndSubmit" element={<PrivateRoute element={SpecialFundReviewAndSubmit} />} />
                        <Route path="/specialFundHistory" element={<PrivateRoute element={SpecialFundHistory} />} />
                        <Route path="/editSpecialFundDetails" element={<PrivateRoute element={EditSpecialFundDetails} />} />
                        <Route path="/moreInfo" element={<PrivateRoute element={MoreInfo} />} />
                        <Route path="/glAdjustmentsListing" element={<PrivateRoute element={GlAdjustmentsListing} />} />
                        <Route path="/enterGLAdjustmentDetails" element={<PrivateRoute element={EnterGLAdjustmentDetails} />} />
                        <Route path="/gLAdjustmentDetails" element={<PrivateRoute element={GLAdjustmentDetails} />} />
                        <Route path="/SelectDLAccounts" element={<PrivateRoute element={SelectDLAccounts} />} />
                        <Route path="/gLReviewSubmit" element={<PrivateRoute element={GLReviewSubmit} />} />
                        <Route path="/gLAdminApproval" element={<PrivateRoute element={GLAdminApproval} />} />
                        <Route path="/vendorInvoiceSummary" element={<PrivateRoute element={VendorInvoiceSummary} />} />
                        <Route path="/vendorInvoiceDetails" element={<PrivateRoute element={VendorInvoiceDetails} />} />
                        <Route path="/invoiceTasksSummary" element={<PrivateRoute element={InvoiceTasksSummary} />} />
                        <Route path="/invoiceTasksDetails" element={<PrivateRoute element={InvoiceTasksDetails} />} />
                        <Route path="/enterInvoiceDetails" element={<PrivateRoute element={EnterInvoiceDetails} />} />
                        <Route path="/selectApprovers" element={<PrivateRoute element={SelectApprovers} />} />
                        <Route path="/invReviewAndSubmit" element={<PrivateRoute element={InvoiceReviewandSubmit} />} />
                        <Route path="/participantIntrestsSummary" element={<PrivateRoute element={ParticipantIntrestsSummary} />} />
                        <Route path="/qboLandingPage" element={<PrivateRoute element={QBOSetup} />} />
                        <Route path="/participantDetails" element={<PrivateRoute element={ParticipantDetails} />} />
                        <Route path="newParticipantDetails" element={<PrivateRoute element={NewParticipantDetails} />} />
                        <Route path="editParticipantDetails" element={<PrivateRoute element={NewParticipantDetails} />} />
                        <Route path="blackBaudTransaction" element={<PrivateRoute element={BlackBaudTransactionLog} />} />
                        <Route path="/blackBaud-Transaction-batch-details" element={<PrivateRoute element={BlackBaudDetailsPage} />} />
                        <Route path="/blackBaud-configuration" element={<PrivateRoute element={BlackBaudConfiguration} />} />
                        <Route path="/invoicesPaymentsTransactions" element={<PrivateRoute element={InvoicesPaymentsTransactions} />} />
                        <Route path="/invoicesPayments" element={<PrivateRoute element={InvoicesPayments} />} />
                        <Route path="/creditLinePayments" element={<PrivateRoute element={CreditLinePayments} />} /> 
                        <Route path="/fundsListing" element={<PrivateRoute element={FundsListing} />} />
                      </Routes>
                    </Box>
                  </ErrorBoundary>
                </Box>
                {(showFooter || !userDetails.isLogin) &&
                  <Footer className="footer" view={view} />
                }
              </Box>
              <TokenRefresher />
            </Suspense>
          </Router>
        </UserProvider>
      </AppThemeProvider>
    </div >
  );
}

export default App;